/*eslint-disable*/
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import {
  Select,
  Input,
  Row,
  Col,
  Typography,
  Button,
  Space,
  Spin,
  Tag,
  Tabs,
  Pagination,
  Popconfirm,
  message,
} from "antd";
import { ReloadOutlined, SearchOutlined, FilterOutlined } from "@ant-design/icons";
import API from "../Utils/API";
import MyTable from "../components/table";
import ViewPreLead from "../components/ViewPreLead";
import { getUsers } from "../Actions/usersAction";
import {
  getAllPreLeads,
  getPreLeadById,
  deletePreLead,
} from "../Actions/preLeadsAction";
import {
  getPreLeadDisplayName,
  getPayloadPhone,
  getPayloadProject,
  getStatusColor,
  getStatusLabel,
  getSourceLabel,
  getUserDisplayName,
  formatPayloadValue,
  formatIst,
  formatIstDate,
  PRE_LEAD_STATUSES,
  PRE_LEAD_SOURCES,
  FOLLOW_UP_FILTERS,
} from "../Utils/preLeadPayload";

const { Title, Text } = Typography;

// Matches the API default. Kept in one place so the `items` param we send and
// the page size the Pagination renders can never drift apart.
const PAGE_SIZE = 10;

const CloudLeads = (props) => {
  const { currentUser, targetId } = props;
  const dispatch = useDispatch();

  const {
    preLeads,
    currentPreLead,
    counts,
    isGetPreLeadsLoading,
    isGetPreLeadByIdLoading,
    isDeletePreLeadLoading,
    totalLength,
  } = useSelector((state) => state.preLeads);
  const users = useSelector((state) => state.users.users);

  const isAdmin = currentUser?.role === "Admin";

  const [tab, setTab] = useState("pending");
  const [source, setSource] = useState("");
  const [followUp, setFollowUp] = useState("");
  const [page, setPage] = useState(1);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [sortField, setSortField] = useState("");
  const [sortOrder, setSortOrder] = useState("");
  const [columnFilters, setColumnFilters] = useState({});
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedPreLead, setSelectedPreLead] = useState(null);

  const triggerRefetch = () => setRefetchTrigger((prev) => prev + 1);

  // Auto-open specific pre-lead detail modal when navigated from Notification
  useEffect(() => {
    if (targetId) {
      setViewOpen(true);
      dispatch(
        getPreLeadById(targetId, (data) => {
          setSelectedPreLead(data);
        })
      );
    }
  }, [targetId]);

  // Track member lead counts when tabs are clicked or pre-fetched
  const [memberCountsMap, setMemberCountsMap] = useState({});

  // Needed for the assign/transfer pickers and the admin team member tabs.
  useEffect(() => {
    dispatch(getUsers());
  }, []);

  // Pre-fetch assigned lead counts for all team members on page load / refetch
  // so counts are visible beforehand without needing to click each tab first.
  useEffect(() => {
    if (!isAdmin || !users || users.length === 0) return;

    users.forEach((member) => {
      API.get(`/pre_leads?user_id=${member.id}&items=1`)
        .then((response) => {
          const total = response?.data?.total_length;
          if (total !== undefined && total !== null) {
            setMemberCountsMap((prev) => ({
              ...prev,
              [member.id]: total,
            }));
          }
        })
        .catch(() => {});
    });
  }, [isAdmin, users, refetchTrigger]);

  useEffect(() => {
    setPage(1);
  }, [tab, source, followUp, columnFilters]);

  useEffect(() => {
    const params = new URLSearchParams();

    // "Unassigned" is an admin filter. Team member tabs filter by user_id.
    if (tab === "unassigned") {
      params.append("unassigned", "true");
    } else if (tab.startsWith("user_")) {
      const userId = tab.replace("user_", "");
      params.append("user_id", userId);
    } else {
      params.append("status", tab);
    }

    // Column-header sort takes priority.
    if (sortField) {
      params.append("sort", sortField);
      params.append("order", sortOrder);
    }

    if (source) {
      params.append("source", source);
    }

    if (followUp) {
      params.append("follow_up", followUp);
    }

    // Column header search & filter inputs (Ransack + Backend Search)
    if (columnFilters) {
      if (columnFilters.id && columnFilters.id[0]) {
        params.append("q[id_eq]", columnFilters.id[0]);
      }
      if (columnFilters.source && columnFilters.source[0]) {
        params.append("source", columnFilters.source[0]);
      }
      if (columnFilters.status && columnFilters.status[0]) {
        params.append("status", columnFilters.status[0]);
      }
      if (columnFilters.name && columnFilters.name[0]) {
        params.append("name", columnFilters.name[0]);
      }
      if (columnFilters.phone && columnFilters.phone[0]) {
        params.append("phone", columnFilters.phone[0]);
      }
      if (columnFilters.assigned_to && columnFilters.assigned_to[0]) {
        params.append("user_id", columnFilters.assigned_to[0]);
      }
      if (columnFilters.project_name && columnFilters.project_name[0]) {
        params.append("project_name", columnFilters.project_name[0]);
      }
    }

    params.append("page", page);
    params.append("items", PAGE_SIZE);

    dispatch(getAllPreLeads(`/pre_leads?${params.toString()}`));
  }, [tab, source, followUp, page, refetchTrigger, sortField, sortOrder, columnFilters]);

  const handleTableChange = (_pagination, filters, sorter) => {
    setColumnFilters(filters || {});

    if (sorter && sorter.field && sorter.order) {
      setSortField(sorter.field);
      setSortOrder(sorter.order === "ascend" ? "asc" : "desc");
    } else {
      setSortField("");
      setSortOrder("");
    }
  };

  const getColumnSearchProps = (dataIndex, title) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          placeholder={`Search ${title}`}
          value={selectedKeys[0]}
          onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => confirm()}
          style={{ marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => confirm()}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => {
              clearFilters();
              confirm();
            }}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
  });

  const openDetail = (record) => {
    // Seed from the row so the modal paints immediately, then refresh from the
    // detail endpoint.
    setSelectedPreLead(record);
    setViewOpen(true);
    dispatch(
      getPreLeadById(record.id, null, () => {
        message.error("Could not load this pre-lead. It may have been removed.");
      }),
    );
  };

  const handleDelete = (id) => {
    dispatch(
      deletePreLead(
        id,
        () => {
          message.success("Pre-lead deleted");
          triggerRefetch();
        },
        (error) => message.error(error || "Could not delete this pre-lead"),
      ),
    );
  };

  const columns = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 80,
      ...getColumnSearchProps("id", "ID"),
    },
    {
      title: "Source",
      dataIndex: "source",
      key: "source",
      width: 110,
      filters: [
        { text: "99acres", value: "99acres" },
        { text: "MagicBricks", value: "magicbricks" },
      ],
      filterMultiple: false,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (source) => getSourceLabel(source),
    },
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      width: 140,
      ellipsis: true,
      ...getColumnSearchProps("name", "Name"),
      render: (_, record) => getPreLeadDisplayName(record),
    },
    {
      title: "Phone",
      dataIndex: "phone",
      key: "phone",
      width: 120,
      ...getColumnSearchProps("phone", "Phone"),
      render: (_, record) => formatPayloadValue(getPayloadPhone(record.payload)),
    },
    // An employee's list is entirely their own rows, so the column is noise.
    ...(isAdmin
      ? [
          {
            title: "Assigned to",
            dataIndex: "assigned_to",
            key: "assigned_to",
            width: 140,
            ellipsis: true,
            filters: (users || []).map((member) => ({
              text: getUserDisplayName(member),
              value: String(member.id),
            })),
            filterMultiple: false,
            filterIcon: (filtered) => (
              <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
            ),
            render: (_, record) =>
              record.assigned_to ? (
                getUserDisplayName(record.assigned_to)
              ) : (
                <Text type="secondary">Unassigned</Text>
              ),
          },
        ]
      : []),
    {
      title: "Follow-up",
      dataIndex: "follow_up_at",
      key: "follow_up_at",
      width: 110,
      sorter: true,
      render: (value) => formatIstDate(value),
    },
    {
      title: "Project Name",
      dataIndex: "project_name",
      key: "project_name",
      width: 140,
      ellipsis: true,
      ...getColumnSearchProps("project_name", "Project"),
      render: (_, record) => formatPayloadValue(getPayloadProject(record.payload)),
    },
    {
      title: "Notes",
      dataIndex: "notes_count",
      key: "notes_count",
      width: 60,
      render: (count) => count || 0,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      filters: PRE_LEAD_STATUSES.filter((s) =>
        ["pending", "dismissed", "positive"].includes(s.value)
      ).map((s) => ({
        text: s.label,
        value: s.value,
      })),
      filterMultiple: false,
      filterIcon: (filtered) => (
        <FilterOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
      ),
      render: (status) =>
        status ? (
          <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
        ) : (
          "-"
        ),
    },
    {
      title: "Received",
      dataIndex: "received_at",
      key: "received_at",
      width: 130,
      sorter: true,
      render: (value) => formatIst(value),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      width: isAdmin ? 130 : 80,
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" onClick={() => openDetail(record)}>
            Open
          </Button>
          {/* Delete is "this was spam, hide it" and is admin-only. Dismiss -
              the everyday action - lives inside the detail modal so the two
              are never adjacent look-alike buttons. */}
          {isAdmin && (
            <Popconfirm
              title="Delete this pre-lead?"
              description="Removes it from Cloud Leads entirely. This cannot be undone."
              okText="Delete"
              cancelText="Cancel"
              onConfirm={() => handleDelete(record.id)}
            >
              <Button type="link" danger>
                Delete
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  // Statuses with no rows are absent from `counts`, not zero.
  const countFor = (key) => counts?.[key] ?? 0;

  useEffect(() => {
    if (tab.startsWith("user_")) {
      const userId = tab.replace("user_", "");
      setMemberCountsMap((prev) => ({
        ...prev,
        [userId]: totalLength,
      }));
    }
  }, [tab, totalLength]);

  const getMemberCount = (member) => {
    if (!member) return 0;
    const id = member.id;
    const strId = String(id);

    // 1. Probe authoritative count keys from backend API counts object
    if (counts) {
      if (counts[id] !== undefined) return counts[id];
      if (counts[strId] !== undefined) return counts[strId];
      if (counts[`user_${id}`] !== undefined) return counts[`user_${id}`];
      if (counts.by_user && counts.by_user[id] !== undefined) return counts.by_user[id];
      if (counts.by_user && counts.by_user[strId] !== undefined) return counts.by_user[strId];
      if (counts.users && counts.users[id] !== undefined) return counts.users[id];
      if (counts.users && counts.users[strId] !== undefined) return counts.users[strId];
      if (counts.user_counts) {
        return counts.user_counts[id] ?? counts.user_counts[strId] ?? 0;
      }
      if (counts.assignees) {
        return counts.assignees[id] ?? counts.assignees[strId] ?? 0;
      }
      // If counts object is loaded and user is missing from user_counts, they have 0 leads
      return 0;
    }

    // 2. Fallback to cached member counts map if available
    if (memberCountsMap[id] !== undefined) return memberCountsMap[id];
    if (memberCountsMap[strId] !== undefined) return memberCountsMap[strId];

    return 0;
  };

  // Include all system users (Admins & Employees) in Team Member tabs
  const teamMembers = users || [];

  const tabItems = [
    { key: "pending", label: `Pending (${countFor("pending")})` },
    { key: "dismissed", label: `Dismissed (${countFor("dismissed")})` },
    { key: "positive", label: `Positive (${countFor("positive")})` },
    ...(isAdmin
      ? [
          {
            key: "unassigned",
            label: `Unassigned${countFor("unassigned") > 0 ? ` (${countFor("unassigned")})` : ""}`,
          },
          ...teamMembers.map((member) => ({
            key: `user_${member.id}`,
            label: `${getUserDisplayName(member)} (${getMemberCount(member)})`,
          })),
        ]
      : []),
  ];

  return (
    <>
      <Row
        className="rowWithPadding"
        justify="space-between"
        align="middle"
        style={{ marginBottom: "10px" }}
      >
        <Col>
          <Title level={4} style={{ marginTop: "2px" }}>
            Cloud Leads
          </Title>
        </Col>
        <Col>
          <Space wrap>
            <Select
              allowClear
              placeholder="All sources"
              style={{ width: 150 }}
              value={source || undefined}
              onChange={(value) => setSource(value || "")}
              options={PRE_LEAD_SOURCES}
            />
            <Select
              allowClear
              placeholder="Any follow-up"
              style={{ width: 150 }}
              value={followUp || undefined}
              onChange={(value) => setFollowUp(value || "")}
              options={FOLLOW_UP_FILTERS}
            />
            <Button icon={<ReloadOutlined />} onClick={triggerRefetch}>
              Refresh
            </Button>
          </Space>
        </Col>
      </Row>

      <Tabs activeKey={tab} onChange={setTab} items={tabItems} />

      <Spin spinning={isGetPreLeadsLoading || isDeletePreLeadLoading}>
        <MyTable
          columns={columns}
          data={preLeads}
          onRowSelect={(record) => openDetail(record)}
          onChange={handleTableChange}
        />
        {totalLength > 0 && (
          <Row justify="end" style={{ marginTop: "16px", marginBottom: "16px" }}>
            <Col>
              <Pagination
                responsive={true}
                size="small"
                current={page}
                pageSize={PAGE_SIZE}
                onChange={(p) => setPage(p)}
                total={totalLength}
                showSizeChanger={false}
              />
            </Col>
          </Row>
        )}
      </Spin>

      <ViewPreLead
        open={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setSelectedPreLead(null);
        }}
        preLead={currentPreLead || selectedPreLead}
        loading={isGetPreLeadByIdLoading}
        currentUser={currentUser}
        onChanged={triggerRefetch}
      />
    </>
  );
};

export default CloudLeads;
